import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {Fragment, forwardRef, useImperativeHandle, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {PickerKeyboardHints, usePickerNavigation} from "../pickers/PickerNavigation";
import {variableChipSx} from "./variableChipStyles";

/**
 * One variable a picker can offer.
 *
 * `sample` and `source` are what the info popover has to say beyond the description, and both are
 * optional because not every surface can answer them: a printout knows its own data, an automation
 * building a message may not.
 *
 * Every string here is already translated — the caller owns the catalogue and therefore owns its
 * keys.
 */
export interface PickerVariable {
    /** The token an author's text stores, and the only thing they could also type by hand. */
    key: string;
    label: string;
    description: string;
    /** What the reader will actually see in place of the token. */
    sample?: string;
    /** Where the value comes from, in words. There is rarely a field path worth quoting. */
    source?: string;
    /** Rows are grouped under this, in the order the variables arrive. */
    scope: string;
}

export interface VariablePickerHandle {
    /** True when the picker consumed the key, so a host driving it can stop there. */
    onKeyDown: (event: KeyboardEvent) => boolean;
}

interface VariablePickerProps {
    variables: PickerVariable[];
    onSelect: (variable: PickerVariable) => void;
    width?: number;
    /**
     * Leave undefined and the picker owns its search field, its focus and its filtering.
     *
     * Pass a query and the host is doing the typing somewhere else — a `/` menu, where the text
     * being matched is in the document — so the field is hidden, `variables` is taken as the final
     * list rather than filtered again, and the host must forward keys through the ref.
     */
    query?: string;
}

/**
 * Pick a variable to drop into authored text.
 *
 * Shared rather than per-feature: surveys, printouts and anything else that lets an author write
 * `{{key}}` into a sentence are the same act, and the second implementation of it is where the two
 * start disagreeing about what a variable looks like.
 *
 * The row is the key chip, then the human label, then an info button. The key leads because it is
 * what the author's text stores and what they will recognise when they see it again; the label is
 * there so a non-technical reader can tell `sectorName` from `serviceLocalizedName` without
 * learning either. Everything else — the sentence, the source, the resolved example — is one click
 * away rather than repeated down every row.
 */
export const VariablePicker = forwardRef<VariablePickerHandle, VariablePickerProps>((
    {variables, onSelect, width = 380, query: hostQuery}: VariablePickerProps,
    ref,
) => {
    const {t} = useTranslation();
    const ownsSearch = hostQuery === undefined;
    const [ownQuery, setOwnQuery] = useState("");
    const query = ownsSearch ? ownQuery : hostQuery;
    const setQuery = setOwnQuery;
    const [searchFocused, setSearchFocused] = useState(false);
    const [detail, setDetail] = useState<{anchor: HTMLElement; variable: PickerVariable} | null>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const items = ownsSearch
        ? variables.filter(variable => {
            const needle = query.trim().toLowerCase();
            if (!needle) return true;
            return [variable.key, variable.label, variable.scope]
                .some(candidate => candidate.toLowerCase().includes(needle));
        })
        : variables;

    const {selectedIndex, listRef, selectFromPointer, handleKey} = usePickerNavigation({
        items,
        onSelect,
        resetKey: query,
    });

    useImperativeHandle(ref, () => ({
        onKeyDown: (event: KeyboardEvent) => handleKey(event.key),
    }));

    const counts = items.reduce<Record<string, number>>((totals, variable) => {
        totals[variable.scope] = (totals[variable.scope] ?? 0) + 1;
        return totals;
    }, {});

    let renderedScope: string | undefined;

    return (
        <Paper
            variant="outlined"
            sx={{width, borderRadius: 2, overflow: "hidden"}}
            onKeyDown={event => {
                if (handleKey(event.key)) event.preventDefault();
            }}
        >
            {/*
              * A borderless field has no edges to aim at, so the whole header row is the hit area
              * and the icon carries the focus state the missing outline used to.
              */}
            {ownsSearch && <><Stack
                direction="row"
                alignItems="center"
                gap={1}
                onClick={() => searchRef.current?.focus()}
                sx={{px: 2, height: 54, flexShrink: 0, cursor: "text"}}
            >
                <SearchIcon
                    fontSize="small"
                    sx={{color: searchFocused ? "text.primary" : "text.disabled"}}
                />
                <InputBase
                    fullWidth
                    autoFocus
                    inputRef={searchRef}
                    placeholder={t("Search variables")}
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    sx={{
                        /* body2 is 12px in this theme — right for a dense row, too quiet for the
                         * one field the picker opens focused into. */
                        typography: "body1",
                        "& input::placeholder": {color: "text.secondary", opacity: 1},
                    }}
                />
                {query && (
                    <IconButton size="small" aria-label={t("Clear search")} onClick={() => setQuery("")}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                )}
            </Stack>
            <Divider/></>}

            <MenuList ref={listRef} disablePadding sx={{maxHeight: 340, overflowY: "auto"}}>
                {items.map((variable, index) => {
                    const startsScope = variable.scope !== renderedScope;
                    renderedScope = variable.scope;

                    return (
                        <Fragment key={variable.key}>
                            {startsScope && (
                                <Stack
                                    component="li"
                                    direction="row"
                                    alignItems="baseline"
                                    gap={0.75}
                                    sx={{px: 2, pt: 1.5, pb: 0.5}}
                                >
                                    <Typography variant="overline" color="text.secondary">
                                        {variable.scope}
                                    </Typography>
                                    <Typography variant="overline" color="text.disabled">
                                        {counts[variable.scope]}
                                    </Typography>
                                </Stack>
                            )}

                            <MenuItem
                                data-index={index}
                                selected={index === selectedIndex}
                                onMouseMove={event => selectFromPointer(index, event.clientX, event.clientY)}
                                onClick={() => onSelect(variable)}
                                sx={{
                                    gap: 1.5,
                                    py: 0.5,
                                    pr: 0.5,
                                    /**
                                     * There is exactly one highlight, and the pointer moves it
                                     * rather than painting a second one: MenuItem's own :hover is
                                     * switched off so hovering one row while the keyboard sits on
                                     * another cannot light up both.
                                     *
                                     * The wash is neutral, not Mui-selected's primary tint — that
                                     * is a commitment colour, and arrowing past a row commits
                                     * to nothing.
                                     */
                                    "&:hover": {backgroundColor: "transparent"},
                                    "&.Mui-selected, &.Mui-selected:hover": {
                                        backgroundColor: "action.hover",
                                    },
                                }}
                            >
                                {/* Leading, the chip is a column, so the labels beside it line up. */}
                                <Box sx={{flexShrink: 0, minWidth: 108}}>
                                    <Box component="span" sx={variableChipSx}>{variable.key}</Box>
                                </Box>

                                <Typography variant="body2" noWrap sx={{flex: 1, minWidth: 0}}>
                                    {variable.label}
                                </Typography>

                                <IconButton
                                    size="small"
                                    aria-label={t("About this variable")}
                                    sx={{flexShrink: 0}}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setDetail({anchor: event.currentTarget, variable});
                                    }}
                                >
                                    <InfoOutlinedIcon fontSize="small"/>
                                </IconButton>
                            </MenuItem>
                        </Fragment>
                    );
                })}

                {items.length === 0 && (
                    <Box sx={{px: 2, py: 3, textAlign: "center"}}>
                        <Typography variant="body2" color="text.secondary">
                            {t("No variable matches \"{{query}}\"", {query: query.trim()})}
                        </Typography>
                    </Box>
                )}
            </MenuList>

            <PickerKeyboardHints/>

            {/*
              * The key first, because it is what their text stores and the one thing they can also
              * type by hand. There is no field path to show underneath it — resolution is a fixed
              * mapping per key, not a lookup along a path.
              */}
            <Popover
                open={Boolean(detail)}
                anchorEl={detail?.anchor}
                onClose={() => setDetail(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: {mt: 0.5, maxWidth: 280, borderRadius: 2}}}}
                /* Instant, like every other surface this picker appears on. */
                transitionDuration={0}
            >
                {detail && (
                    <Stack gap={1} sx={{p: 2}}>
                        <Box>
                            <Box component="span" sx={variableChipSx}>{detail.variable.key}</Box>
                        </Box>
                        <Typography variant="body2">{detail.variable.description}</Typography>
                        {detail.variable.source && (
                            <Typography variant="body2" color="text.secondary">
                                {detail.variable.source}
                            </Typography>
                        )}
                        {detail.variable.sample && (
                            <Typography variant="body2" color="text.secondary">
                                {t("Shows as \"{{sample}}\"", {sample: detail.variable.sample})}
                            </Typography>
                        )}
                    </Stack>
                )}
            </Popover>
        </Paper>
    );
});

VariablePicker.displayName = "VariablePicker";

export default VariablePicker;
